import { StoreDetails } from '../../data/types/stores';
const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

type WorkingHours = {
  working_time_sun_start: string;
  working_time_sun_end: string;
  working_time_mon_start: string;
  working_time_mon_end: string;
  working_time_tue_start: string;
  working_time_tue_end: string;
  working_time_wed_start: string;
  working_time_wed_end: string;
  working_time_thu_start: string;
  working_time_thu_end: string;
  working_time_fri_start: string;
  working_time_fri_end: string;
  working_time_sat_start: string;
  working_time_sat_end: string;
};

export function getOpeningHours(currentStore: StoreDetails) {
  const workingHours: WorkingHours = {
    working_time_sun_start: currentStore.working_time_sun_start,
    working_time_sun_end: currentStore.working_time_sun_end,
    working_time_mon_start: currentStore.working_time_mon_start,
    working_time_mon_end: currentStore.working_time_mon_end,
    working_time_tue_start: currentStore.working_time_tue_start,
    working_time_tue_end: currentStore.working_time_tue_end,
    working_time_wed_start: currentStore.working_time_wed_start,
    working_time_wed_end: currentStore.working_time_wed_end,
    working_time_thu_start: currentStore.working_time_thu_start,
    working_time_thu_end: currentStore.working_time_thu_end,
    working_time_fri_start: currentStore.working_time_fri_start,
    working_time_fri_end: currentStore.working_time_fri_end,
    working_time_sat_start: currentStore.working_time_sat_start,
    working_time_sat_end: currentStore.working_time_sat_end,
  };

  const openingHours: Record<string, string> = {};

  // Iterate through each day of the week
  for (const day of daysOfWeek) {
    const startTime: string =
      workingHours[
        `working_time_${day.toLowerCase()}_start` as keyof WorkingHours
      ];
    const endTime: string =
      workingHours[
        `working_time_${day.toLowerCase()}_end` as keyof WorkingHours
      ];

    // Check if the store is closed for the current day
    if (startTime === endTime) {
      openingHours[day] = 'Closed';
    } else {
      // Store the opening and closing hours for the current day
      openingHours[day] = `${startTime} - ${endTime}`;
    }
  }

  // Combine similar opening hours for consecutive days
  const combinedOpeningHours: string[] = [];
  let startDay = '';

  for (let i = 0; i < daysOfWeek.length; i++) {
    const currentDay = daysOfWeek[i];
    const nextDay = daysOfWeek[i + 1];

    if (openingHours[currentDay] !== openingHours[nextDay]) {
      if (startDay !== '') {
        combinedOpeningHours.push(
          `${startDay} to ${currentDay} ${openingHours[startDay]}`,
        );
        startDay = '';
      } else {
        combinedOpeningHours.push(`${currentDay} ${openingHours[currentDay]}`);
      }
    } else {
      if (startDay === '') {
        startDay = currentDay;
      }
    }
  }

  return combinedOpeningHours.join('\n');
}

export function isStoreOpen(currentStore: StoreDetails): boolean {
  const daysOfWeek = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

  const now = new Date();
  const currentDayOfWeek = daysOfWeek[now.getDay()];
  const currentTime = now.getHours() * 60 + now.getMinutes();

  const startTimeKey = `working_time_${currentDayOfWeek}_start`;
  const endTimeKey = `working_time_${currentDayOfWeek}_end`;

  const startTime = currentStore[startTimeKey as keyof StoreDetails];
  const endTime = currentStore[endTimeKey as keyof StoreDetails];

  // Check if the store is closed for the current day
  if (startTime === '00:00' && endTime === '00:00') {
    return false;
  }

  // Convert start and end times to minutes for easier comparison
  const startMinutes = getMinutesFromTimeString(startTime as string);
  const endMinutes = getMinutesFromTimeString(endTime as string);

  // Check if the current time falls within the opening hours
  return currentTime >= startMinutes && currentTime <= endMinutes;
}

function getMinutesFromTimeString(timeString: string): number {
  const [hours, minutes] = timeString.split(':').map(Number);
  return hours * 60 + minutes;
}
